import { useEffect, useMemo, useState } from 'react'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectMainAttachmentsState } from '@/msgdeskRuntime/state'

// Main attachments gallery structure. The surface supplies its visual class
// tokens through the skin prop (25_SHARED_DESKUI_SKELETON.md §4.6); md-*
// contract classes stay skeleton-owned.

export interface MainAttachmentsSkin {
  empty: string
  root: string
  header: string
  title: string
  count: string
  body: string
  preview: string
  image: string
  side: string
  meta: string
  metaTitle: string
  metaDescription: string
  nav: string
  button: string
  thumbs: string
  thumb: string
  thumbSelected: string
  thumbUnselected: string
  thumbImage: string
}

function fallbackTitle(filename?: string) {
  return filename || 'Attachment'
}

export function MainAttachments({ skin }: { skin: MainAttachmentsSkin }) {
  const { state } = useSubsStore()
  const attachments = useMemo(() => selectMainAttachmentsState(state), [state])
  const [selectedId, setSelectedId] = useState<string>('')

  useEffect(() => {
    if (!attachments.length) {
      setSelectedId('')
      return
    }
    if (!attachments.some(attachment => attachment.id === selectedId)) {
      setSelectedId(attachments[0].id)
    }
  }, [attachments, selectedId])

  const selectedIndex = Math.max(0, attachments.findIndex(attachment => attachment.id === selectedId))
  const selected = attachments[selectedIndex] || attachments[0]

  if (!attachments.length) {
    return (
      <div className={`md-main-attachments ${skin.empty}`}>
        No attachments
      </div>
    )
  }

  function selectOffset(offset: number) {
    const nextIndex = (selectedIndex + offset + attachments.length) % attachments.length
    setSelectedId(attachments[nextIndex].id)
  }

  return (
    <div className={`md-main-attachments ${skin.root}`}>
      <div className={`md-main-attachments__header ${skin.header}`}>
        <div className={`md-main-attachments__title ${skin.title}`}>Attachments</div>
        <div className={`md-main-attachments__count ${skin.count}`}>
          {selectedIndex + 1}/{attachments.length}
        </div>
      </div>
      <div className={`md-main-attachments__body ${skin.body}`}>
        <div className={`md-main-attachments__preview ${skin.preview}`}>
          <img
            className={`md-main-attachments__image ${skin.image}`}
            src={selected.src}
            alt={fallbackTitle(selected.title || selected.filename)}
            width={selected.width}
            height={selected.height}
          />
        </div>
        <div className={`md-main-attachments__side ${skin.side}`}>
          <div className={`md-main-attachments__meta ${skin.meta}`}>
            <div className={`md-main-attachments__meta-title ${skin.metaTitle}`}>
              {fallbackTitle(selected.title || selected.filename)}
            </div>
            {selected.description ? (
              <div className={`md-main-attachments__meta-description ${skin.metaDescription}`}>
                {selected.description}
              </div>
            ) : null}
          </div>
          <div className={`md-main-attachments__nav ${skin.nav}`}>
            <button className={`md-main-attachments__button ${skin.button}`} type="button" onClick={() => selectOffset(-1)}>
              Prev
            </button>
            <button className={`md-main-attachments__button ${skin.button}`} type="button" onClick={() => selectOffset(1)}>
              Next
            </button>
          </div>
          <div className={`md-main-attachments__thumbs ${skin.thumbs}`}>
            {attachments.map((attachment, index) => {
              const isSelected = attachment.id === selected.id
              return (
                <button
                  key={`${attachment.id}-${index}`}
                  className={`md-main-attachments__thumb ${skin.thumb} ${isSelected ? skin.thumbSelected : skin.thumbUnselected}`}
                  type="button"
                  data-md-attachment-id={attachment.id}
                  onClick={() => setSelectedId(attachment.id)}
                >
                  <img className={skin.thumbImage} src={attachment.thumbSrc} alt={fallbackTitle(attachment.filename)} />
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
